import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { composeValidators, required as requiredValidator } from '../../util/validators';
import {
  MAILING_ADDRESS_COUNTRIES,
  getMailingAddressRegionOptions,
  isMailingAddressPostalCodeValid,
  isMailingAddressRegionValid,
} from '../../util/mailingAddress';

import FieldSelect from '../FieldSelect/FieldSelect';
import FieldTextInput from '../FieldTextInput/FieldTextInput';

import css from './MailingAddressFields.module.css';

const fieldName = (prefix, name) => `${prefix}.${name}`;

/**
 * Country-aware mailing address fields for a React Final Form form.
 *
 * @param {Object} props
 * @param {Object} props.form React Final Form form API
 * @param {Object} props.values Current form values
 * @param {string} [props.namePrefix] Nested value path
 * @param {string} [props.formId] Prefix for input IDs
 * @param {string} [props.className] Optional wrapper class
 * @param {boolean} [props.isRequired] Whether a complete address is required
 * @returns {JSX.Element}
 */
const MailingAddressFields = props => {
  const {
    className,
    form,
    formId = 'MailingAddress',
    isRequired = true,
    namePrefix = 'mailingAddress',
    values,
  } = props;
  const intl = useIntl();
  const address = values?.[namePrefix] || {};
  const countryCode = address.countryCode || 'US';
  const regionOptions = getMailingAddressRegionOptions(countryCode);

  const requiredMessage = intl.formatMessage({ id: 'MailingAddressFields.required' });
  const invalidCountryMessage = intl.formatMessage({
    id: 'MailingAddressFields.countryInvalid',
  });
  const invalidRegionMessage = intl.formatMessage({ id: 'MailingAddressFields.regionInvalid' });
  const invalidPostalCodeMessage = intl.formatMessage({
    id:
      countryCode === 'CA'
        ? 'MailingAddressFields.canadianPostalCodeInvalid'
        : 'MailingAddressFields.usZipCodeInvalid',
  });
  const requiredField = isRequired ? requiredValidator(requiredMessage) : () => undefined;
  const countryValidator = value =>
    MAILING_ADDRESS_COUNTRIES.some(country => country.key === value)
      ? undefined
      : invalidCountryMessage;
  const regionValidator = value =>
    !value || isMailingAddressRegionValid(value, countryCode) ? undefined : invalidRegionMessage;
  const postalCodeValidator = value =>
    !value || isMailingAddressPostalCodeValid(value, countryCode)
      ? undefined
      : invalidPostalCodeMessage;

  const handleCountryChange = nextCountryCode => {
    const currentRegion = form.getState().values?.[namePrefix]?.region;
    if (currentRegion && !isMailingAddressRegionValid(currentRegion, nextCountryCode)) {
      form.change(fieldName(namePrefix, 'region'), '');
    }
  };

  return (
    <div className={classNames(css.root, className)} data-mixpanel-sensitive>
      <FieldTextInput
        className={css.fullWidthField}
        type="text"
        id={`${formId}.addressLine1`}
        name={fieldName(namePrefix, 'addressLine1')}
        autoComplete="address-line1"
        label={intl.formatMessage({ id: 'MailingAddressFields.addressLine1Label' })}
        validate={requiredField}
      />
      <FieldTextInput
        className={css.fullWidthField}
        type="text"
        id={`${formId}.addressLine2`}
        name={fieldName(namePrefix, 'addressLine2')}
        autoComplete="address-line2"
        label={intl.formatMessage({ id: 'MailingAddressFields.addressLine2Label' })}
      />
      <div className={css.twoColumnRow}>
        <FieldTextInput
          className={css.columnField}
          type="text"
          id={`${formId}.city`}
          name={fieldName(namePrefix, 'city')}
          autoComplete="address-level2"
          label={intl.formatMessage({ id: 'MailingAddressFields.cityLabel' })}
          validate={requiredField}
        />
        <FieldSelect
          className={css.columnField}
          id={`${formId}.region`}
          name={fieldName(namePrefix, 'region')}
          autoComplete="address-level1"
          label={intl.formatMessage({
            id:
              countryCode === 'CA'
                ? 'MailingAddressFields.provinceTerritoryLabel'
                : 'MailingAddressFields.stateLabel',
          })}
          validate={composeValidators(requiredField, regionValidator)}
        >
          <option value="">
            {intl.formatMessage({
              id:
                countryCode === 'CA'
                  ? 'MailingAddressFields.provinceTerritoryPlaceholder'
                  : 'MailingAddressFields.statePlaceholder',
            })}
          </option>
          {regionOptions.map(region => (
            <option key={region.key} value={region.key}>
              {region.label}
            </option>
          ))}
        </FieldSelect>
      </div>
      <div className={css.twoColumnRow}>
        <FieldTextInput
          className={css.columnField}
          type="text"
          id={`${formId}.postalCode`}
          name={fieldName(namePrefix, 'postalCode')}
          autoComplete="postal-code"
          autoCapitalize={countryCode === 'CA' ? 'characters' : undefined}
          label={intl.formatMessage({
            id:
              countryCode === 'CA'
                ? 'MailingAddressFields.postalCodeLabel'
                : 'MailingAddressFields.zipCodeLabel',
          })}
          validate={composeValidators(requiredField, postalCodeValidator)}
        />
        <FieldSelect
          className={css.columnField}
          id={`${formId}.countryCode`}
          name={fieldName(namePrefix, 'countryCode')}
          autoComplete="country"
          label={intl.formatMessage({ id: 'MailingAddressFields.countryLabel' })}
          validate={composeValidators(requiredField, countryValidator)}
          onChange={handleCountryChange}
        >
          {MAILING_ADDRESS_COUNTRIES.map(country => (
            <option key={country.key} value={country.key}>
              {country.label}
            </option>
          ))}
        </FieldSelect>
      </div>
    </div>
  );
};

export default MailingAddressFields;
